"use client"

import { FilterData } from "@/app/(root)/all-products/_components/all-products-left"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import { useEffect, useState } from "react"
import { AppliedFilters } from "./applied-filters"
import { FilterCategory } from "./filter-category"
import { FilterSkeleton } from "./filter-skeleton"
import { useRouter } from "next/navigation"

// New types based on the backend API
export type FilterOptionWithImages = {
  value: string
  label: string
  selected?: string
  unselected?: string
}

export type Filter = {
  id?: string
  title: string
  type: "checkbox" | "range"
  options: FilterOptionWithImages[]
}

export type FilterState = {
  [filterId: string]: string[] | { min: number; max: number }
}

interface FilterSectionProps {
  className?: string
  initialFilters: FilterState
  data?: FilterData
  // loading?: boolean
  onFilterChange?: (filters: FilterState) => void
}

export function FilterSection({
  initialFilters,
  // loading = false,
  onFilterChange,
  data,
  className,
}: FilterSectionProps) {
  const router = useRouter()
  const [activeFilters, setActiveFilters] =
    useState<FilterState>(initialFilters)

  // Sync with prop changes
  useEffect(() => {
    if (JSON.stringify(initialFilters) !== JSON.stringify(activeFilters)) {
      setActiveFilters(initialFilters)
    }
  }, [initialFilters])

  const processedFilters =
    data?.filters?.map((filter) => ({
      ...filter,
      type: filter?.type || ("checkbox" as const),
      options: filter?.options?.map((option) => ({
        ...option,
        label: option?.label || option.value,
      })),
    })) || []

  const [expandedCategories, setExpandedCategories] = useState<
    Record<string, boolean>
  >(
    processedFilters.reduce((acc, filter) => {
      acc[filter?.id] = true // Default to expanded
      return acc
    }, {} as Record<string, boolean>)
  )

  const hasActiveFilters = Object.values(activeFilters).some((value) => {
    if (Array.isArray(value) && value?.length > 0) return true
    if (typeof value === "object" && "min" in value && "max" in value) {
      return true
    }
    return false
  })

  const handleFilterChange = (
    filterId: string,
    value: string[] | { min: number; max: number }
  ) => {
    const newFilters = { ...activeFilters, [filterId]: value }

    // Remove empty filters
    if (Array.isArray(value) && value.length === 0) {
      delete newFilters[filterId]
    }

    setActiveFilters(newFilters)
    onFilterChange?.(newFilters)
    // router.refresh()
  }

  const handleRemoveFilter = (filterId: string, optionValue?: string) => {
    const newFilters = { ...activeFilters }

    if (optionValue && Array.isArray(newFilters[filterId])) {
      // Remove specific option from array
      newFilters[filterId] = (newFilters[filterId] as string[]).filter(
        (value) => value !== optionValue
      )

      // Remove category if no options left
      if ((newFilters[filterId] as string[]).length === 0) {
        delete newFilters[filterId]
      }
    } else {
      // Remove entire category
      delete newFilters[filterId]
    }

    setActiveFilters(newFilters)
    onFilterChange?.(newFilters)
  }

  const handleClearAll = () => {
    setActiveFilters({})
    onFilterChange?.({})
  }

  const toggleCategory = (filterId: string) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [filterId]: !prev[filterId],
    }))
  }

  if (!data?.filters) {
    return <FilterSkeleton />
  }

  return (
    <div
      className={cn(
        "w-full bg-[#F7F7F7] px-2 py-2 rounded-2xl h-fit",
        className
      )}
    >
      <div className="flex items-center justify-between mb-4  w-full">
        <h2 className="text-lg p-2 font-gilroyMedium">Filters</h2>
        {hasActiveFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClearAll}
            className="text-sm text-muted-foreground font-gilroyMedium"
          >
            Clear All
          </Button>
        )}
      </div>

      {hasActiveFilters && (
        <div className="mb-4 w-full">
          <AppliedFilters
            filters={activeFilters}
            filterDefinitions={processedFilters}
            onRemove={handleRemoveFilter}
          />
        </div>
      )}

      <div className="space-y-2 w-full">
        {processedFilters.map((filter) => (
          <div key={filter.title}>
            <FilterCategory
              filter={filter}
              expanded={expandedCategories[filter.title]}
              onToggleExpand={() => toggleCategory(filter.title)}
              selectedValues={activeFilters[filter.title]}
              onChange={(values) => handleFilterChange(filter.title, values)}
            />
          </div>
        ))}
      </div>
    </div>
  )
}
